import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, IconButton, TextField, Button, Avatar, Divider, List, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import buxferTransactions from '../services/buxfer.service';

function storageKey(id) {
  return `buxfer_notes_${id}`;
}

export default function NotesSidebar({ open, onClose, account }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState('');
  const [editingIdx, setEditingIdx] = useState(-1);
  const [editingText, setEditingText] = useState('');

  useEffect(() => {
    if (account && open) {
      // Try fetching from server first
      buxferTransactions.listNotes(account.id).then(res => {
        if (res && res.success) {
          // normalize server fields to frontend fields
          const mapped = (res.result || []).map(n => ({ ...n, at: n.created_at || n.at, editedAt: n.edited_at || n.editedAt }));
          setNotes(mapped);
          localStorage.setItem(storageKey(account.id), JSON.stringify(mapped));
        } else {
          // fallback
          const raw = localStorage.getItem(storageKey(account.id));
          try { setNotes(raw ? JSON.parse(raw) : []); } catch (e) { setNotes([]); }
        }
      }).catch(() => {
        const raw = localStorage.getItem(storageKey(account.id));
        try { setNotes(raw ? JSON.parse(raw) : []); } catch (e) { setNotes([]); }
      });
    } else {
      setNotes([]);
    }
    setText('');
    setEditingIdx(-1);
    setEditingText('');
  }, [account, open]);

  const postNote = () => {
    if (!text.trim()) return;
  const payload = { author: 'You', text: text.trim() };
    // Optimistic UI
    const newNote = { ...payload, at: new Date().toISOString() };
    const next = [newNote, ...notes];
    setNotes(next);
    setText('');

    buxferTransactions.createNote(account.id, payload.author, payload.text).then(res => {
      if (res && res.success && res.result) {
        // replace optimistic note with server note (has id)
        const serverNote = res.result;
        const mappedServer = { ...serverNote, at: serverNote.created_at || serverNote.at, editedAt: serverNote.edited_at || serverNote.editedAt };
        const replaced = [mappedServer, ...notes.slice(1)];
        setNotes(replaced);
        localStorage.setItem(storageKey(account.id), JSON.stringify(replaced));
      } else {
        // fallback persist locally
        localStorage.setItem(storageKey(account.id), JSON.stringify(next));
      }
    }).catch(() => {
      localStorage.setItem(storageKey(account.id), JSON.stringify(next));
    });
  };

  const startEdit = (idx) => {
    setEditingIdx(idx);
    setEditingText(notes[idx].text);
  };

  const saveEdit = () => {
    if (editingIdx < 0) return;
    const note = notes[editingIdx];
    const oldText = note.text;
    const updated = { ...note, text: editingText, editedAt: new Date().toISOString() };
    const copy = [...notes];
    copy[editingIdx] = updated;
    setNotes(copy);
    setEditingIdx(-1);
    setEditingText('');

    if (note.id) {
      buxferTransactions.updateNote(note.id, editingText).then(res => {
        if (res && res.success && res.result) {
          const serverRes = res.result;
          const mapped = { ...serverRes, at: serverRes.created_at || serverRes.at, editedAt: serverRes.edited_at || serverRes.editedAt };
          const serverCopy = copy.map(n => n.id === mapped.id ? mapped : n);
          setNotes(serverCopy);
          localStorage.setItem(storageKey(account.id), JSON.stringify(serverCopy));
        }
      }).catch(() => {
        // revert to old text on failure
        const reverted = [...notes];
        reverted[editingIdx] = { ...note, text: oldText };
        setNotes(reverted);
        localStorage.setItem(storageKey(account.id), JSON.stringify(reverted));
      });
    } else {
      // offline note - just persist
      localStorage.setItem(storageKey(account.id), JSON.stringify(copy));
    }
  };

  const cancelEdit = () => {
    setEditingIdx(-1);
    setEditingText('');
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 480, p: 2, bgcolor: '#121212', color: 'white', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{account ? account.name : 'Notes'}</Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mt: 2 }}>
          <TextField
            multiline
            minRows={3}
            placeholder="Write an update and mention others with @"
            fullWidth
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={{ bgcolor: '#1e1e1e', borderRadius: 1, input: { color: 'white' } }}
            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button onClick={() => setText('')} sx={{ color: 'rgba(255,255,255,0.7)' }}>CLEAR</Button>
            <Button variant="contained" onClick={postNote} sx={{ bgcolor: '#1976d2', color: 'white' }}>POST</Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2, bgcolor: '#2c2c2c' }} />

        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {notes.length === 0 && <Typography variant="body2" color="gray">No updates yet.</Typography>}
          <List>
            {notes.map((n, idx) => (
              <ListItem key={idx} alignItems="flex-start" sx={{ alignItems: 'flex-start', bgcolor: '#151515', borderRadius: 1, mb: 1, p: 1 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: '#424242' }}>{(n.author || 'U').slice(0,1)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography sx={{ fontWeight: 'bold' }}>{n.author || 'User'}</Typography>
                      <Typography variant="caption" sx={{ color: 'gray' }}>{n.editedAt ? `Edited ${new Date(n.editedAt).toLocaleString()}` : new Date(n.at).toLocaleString()}</Typography>
                    </Box>
                  }
                  secondary={
                    editingIdx === idx ? (
                      <Box>
                        <TextField fullWidth multiline minRows={2} value={editingText} onChange={(e) => setEditingText(e.target.value)} sx={{ bgcolor: '#1e1e1e', input: { color: 'white' } }} />
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button onClick={cancelEdit} sx={{ color: 'rgba(255,255,255,0.7)' }}>Cancel</Button>
                          <Button variant="contained" onClick={saveEdit} sx={{ bgcolor: '#1976d2', color: 'white' }}>Save</Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography sx={{ whiteSpace: 'pre-wrap', color: 'white' }}>{n.text}</Typography>
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <Button size="small" onClick={() => startEdit(idx)} sx={{ color: 'rgba(255,255,255,0.7)' }}>Edit</Button>
                          <Button size="small" onClick={() => {
                            // delete
                            const note = notes[idx];
                            const prev = notes;
                            const next = prev.filter((_, i) => i !== idx);
                            setNotes(next);
                            buxferTransactions.deleteNote(note.id).then(res => {
                              if (res && res.success) {
                                localStorage.setItem(storageKey(account.id), JSON.stringify(next));
                              } else {
                                localStorage.setItem(storageKey(account.id), JSON.stringify(next));
                              }
                            }).catch(() => {
                              // fallback: persist updated list locally
                              localStorage.setItem(storageKey(account.id), JSON.stringify(next));
                            });
                          }} sx={{ color: 'rgba(255,255,255,0.7)' }}>Delete</Button>
                        </Box>
                      </Box>
                    )
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Drawer>
  );
}
